import React, { useState, useEffect, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  InputAdornment,
  Collapse,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material"
import { Link as RouterLink } from "react-router-dom"
import HomeIcon from "@mui/icons-material/Home"
import VpnKeyIcon from "@mui/icons-material/VpnKey"
import SearchIcon from "@mui/icons-material/Search"
import RefreshIcon from "@mui/icons-material/Refresh"
import AddIcon from "@mui/icons-material/Add"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import BarChartIcon from "@mui/icons-material/BarChart"
import LockOpenIcon from "@mui/icons-material/LockOpen"
import BlockIcon from "@mui/icons-material/Block"
import MemoryIcon from "@mui/icons-material/Memory"
import ReceiptIcon from "@mui/icons-material/Receipt"
import LoginIcon from "@mui/icons-material/Login"
import LogoutIcon from "@mui/icons-material/Logout"

import {
  clLogin,
  clGet,
  clGetPrices,
  clPost,
  getCLToken,
  clearCLToken,
  getCLTokenExpiry,
} from "../../helpers/cl_api"

// ─── TabPanel ────────────────────────────────────────────────────────────────

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(str) {
  if (!str) return "-"
  const d = new Date(str)
  if (isNaN(d)) return str
  return (
    d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }) +
    " " +
    d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
  )
}

const tierColors = {
  viewer: "#6366f1",
  starter: "#3b82f6",
  basic: "#10b981",
  trader: "#0ea5e9",
  pro: "#d97706",
  unlimited: "#8b5cf6",
  enterprise: "#dc2626",
}

function TierChip({ tier }) {
  const color = tierColors[tier] || "#6b7280"
  return (
    <Chip
      label={tier}
      size="small"
      sx={{
        height: 22,
        fontSize: "0.7rem",
        fontWeight: 600,
        textTransform: "uppercase",
        bgcolor: `${color}18`,
        color,
        border: `1px solid ${color}30`,
      }}
    />
  )
}

function StatusChip({ active }) {
  return (
    <Chip
      label={active ? "Active" : "Inactive"}
      size="small"
      sx={{
        height: 22,
        fontSize: "0.7rem",
        fontWeight: 600,
        bgcolor: active ? "#10b98118" : "#ef444418",
        color: active ? "#10b981" : "#ef4444",
        border: `1px solid ${active ? "#10b98130" : "#ef444430"}`,
      }}
    />
  )
}

// ─── Login Card ──────────────────────────────────────────────────────────────

function CLLoginCard({ onLogin }) {
  const { t } = useTranslation()
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setLoading(true)
    try {
      await clLogin(username, password)
      onLogin()
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 400,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 4,
          width: 380,
          borderRadius: 2,
          border: "1px solid #e5e7eb",
        }}
      >
        <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <VpnKeyIcon sx={{ fontSize: 40, color: "#3b82f6" }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#111827" }}>
            {t("Cloud Licence Admin")}
          </Typography>
          <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
            {t("Sign in to manage licences")}
          </Typography>
        </Stack>
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              size="small"
              label={t("Username")}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              fullWidth
              autoFocus
            />
            <TextField
              size="small"
              label={t("Password")}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
            />
            {error && (
              <Typography sx={{ fontSize: "0.813rem", color: "#ef4444" }}>
                {error}
              </Typography>
            )}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <LoginIcon />
                )
              }
              sx={{
                textTransform: "none",
                bgcolor: "#3b82f6",
                "&:hover": { bgcolor: "#2563eb" },
              }}
            >
              {t("Sign In")}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  )
}

// ─── Create License Dialog ───────────────────────────────────────────────────

function CreateLicenseDialog({ open, onClose, onCreated, tiers }) {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    tier: "",
    ownerName: "",
    ownerEmail: "",
    expiresAt: "",
    customKey: "",
  })
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open) {
      const tierKeys = Object.keys(tiers || {})
      const d = new Date()
      d.setFullYear(d.getFullYear() + 1)
      setForm({
        tier: tierKeys[0] || "",
        ownerName: "",
        ownerEmail: "",
        expiresAt: d.toISOString().split("T")[0],
        customKey: "",
      })
      setError("")
    }
  }, [open, tiers])

  const handleCreate = async () => {
    setError("")
    setLoading(true)
    try {
      const body = { tier: form.tier }
      if (form.ownerName) body.ownerName = form.ownerName
      if (form.ownerEmail) body.ownerEmail = form.ownerEmail
      if (form.expiresAt)
        body.expiresAt = new Date(form.expiresAt).toISOString()
      if (form.customKey) body.customKey = form.customKey
      const data = await clPost("/license/create", body)
      onCreated(data.license)
      onClose()
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>{t("Create New License")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            size="small"
            label={t("Tier")}
            value={form.tier}
            onChange={(e) => setForm({ ...form, tier: e.target.value })}
            fullWidth
          >
            {Object.entries(tiers || {}).map(([key, price]) => (
              <MenuItem key={key} value={key}>
                {key.charAt(0).toUpperCase() + key.slice(1)} - €{price}
              </MenuItem>
            ))}
          </TextField>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                size="small"
                label={t("Owner Name")}
                value={form.ownerName}
                onChange={(e) =>
                  setForm({ ...form, ownerName: e.target.value })
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                label={t("Owner Email")}
                type="email"
                value={form.ownerEmail}
                onChange={(e) =>
                  setForm({ ...form, ownerEmail: e.target.value })
                }
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                size="small"
                label={t("Expires At")}
                type="date"
                value={form.expiresAt}
                onChange={(e) =>
                  setForm({ ...form, expiresAt: e.target.value })
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                label={t("Custom Key (optional)")}
                value={form.customKey}
                onChange={(e) =>
                  setForm({ ...form, customKey: e.target.value })
                }
                placeholder="XXXX-XXXX-XXXX-XXXX"
                fullWidth
              />
            </Grid>
          </Grid>
          {error && (
            <Typography sx={{ fontSize: "0.813rem", color: "#ef4444" }}>
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          {t("Cancel")}
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={loading || !form.tier}
          sx={{
            textTransform: "none",
            bgcolor: "#10b981",
            "&:hover": { bgcolor: "#059669" },
          }}
        >
          {loading ? <CircularProgress size={18} color="inherit" /> : t("Create")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

const MuiCloudLicences = () => {
  const { t } = useTranslation()
  document.title = t("Cloud Licences") + " | FlexFlow ERP"

  // Auth state
  const [authenticated, setAuthenticated] = useState(!!getCLToken())
  const [sessionMinutes, setSessionMinutes] = useState(0)

  // Data
  const [licences, setLicences] = useState([])
  const [orders, setOrders] = useState([])
  const [tiers, setTiers] = useState({})
  const [loading, setLoading] = useState(false)

  // UI
  const [activeTab, setActiveTab] = useState(0)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [orderSearch, setOrderSearch] = useState("")
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  })

  // Session timer
  useEffect(() => {
    if (!authenticated) return
    const interval = setInterval(() => {
      const expiry = getCLTokenExpiry()
      const remaining = Math.max(0, Math.floor((expiry - Date.now()) / 60000))
      setSessionMinutes(remaining)
      if (remaining <= 0) {
        clearCLToken()
        setAuthenticated(false)
      }
    }, 10000)
    // Run immediately
    const expiry = getCLTokenExpiry()
    setSessionMinutes(Math.max(0, Math.floor((expiry - Date.now()) / 60000)))
    return () => clearInterval(interval)
  }, [authenticated])

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [licData, ordData, priceData] = await Promise.all([
        clGet("/license/list"),
        clGet("/checkout/orders").catch(() => ({ orders: [] })),
        clGetPrices(),
      ])
      setLicences(licData.licenses || [])
      setOrders(ordData.orders || [])
      setTiers(priceData.tiers || {})
    } catch (err) {
      if (err.message === "Session expired" || err.message === "Not authenticated") {
        setAuthenticated(false)
      } else {
        setSnackbar({ open: true, message: err.message, severity: "error" })
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (authenticated) loadData()
  }, [authenticated, loadData])

  const handleLogin = () => {
    setAuthenticated(true)
  }

  const handleLogout = () => {
    clearCLToken()
    setAuthenticated(false)
    setLicences([])
    setOrders([])
  }

  // Licence actions
  const handleToggleLicense = async (key, activate) => {
    try {
      const endpoint = activate
        ? "/license/activate"
        : "/license/deactivate"
      await clPost(endpoint, { licenseKey: key })
      setSnackbar({
        open: true,
        message: `License ${activate ? "activated" : "deactivated"}`,
        severity: "success",
      })
      loadData()
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" })
    }
  }

  const handleResetHardware = async (key) => {
    try {
      await clPost("/license/reset-hardware", { licenseKey: key })
      setSnackbar({
        open: true,
        message: "Hardware binding reset",
        severity: "success",
      })
      loadData()
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" })
    }
  }

  // Analytics
  const stats = useMemo(() => {
    const total = licences.length
    const active = licences.filter((l) => l.isActive).length
    const inactive = total - active
    const bound = licences.filter((l) => l.hardwareId).length
    return { total, active, inactive, bound }
  }, [licences])

  const orderStats = useMemo(() => {
    const total = orders.length
    const completed = orders.filter((o) => o.status === "completed").length
    const pending = total - completed
    const revenue = orders
      .filter((o) => o.status === "completed")
      .reduce((sum, o) => sum + (o.totalAmount || 0), 0)
    return { total, completed, pending, revenue }
  }, [orders])

  // Filtered licences
  const filteredLicences = useMemo(() => {
    let result = licences
    if (statusFilter === "active")
      result = result.filter((l) => l.isActive)
    if (statusFilter === "inactive")
      result = result.filter((l) => !l.isActive)
    if (searchTerm) {
      const q = searchTerm.toLowerCase()
      result = result.filter(
        (l) =>
          (l.licenseKey || "").toLowerCase().includes(q) ||
          (l.ownerEmail || "").toLowerCase().includes(q) ||
          (l.ownerName || "").toLowerCase().includes(q) ||
          (l.tier || "").toLowerCase().includes(q)
      )
    }
    return result
  }, [licences, statusFilter, searchTerm])

  // Filtered orders
  const filteredOrders = useMemo(() => {
    if (!orderSearch) return orders
    const q = orderSearch.toLowerCase()
    return orders.filter(
      (o) =>
        (o.orderId || "").toLowerCase().includes(q) ||
        (o.customerEmail || "").toLowerCase().includes(q) ||
        (o.customerName || "").toLowerCase().includes(q) ||
        (o.licenseKey || "").toLowerCase().includes(q)
    )
  }, [orders, orderSearch])

  // ─── Render ──────────────────────────────────────────────────────────────

  if (!authenticated) {
    return (
      <Box sx={{ py: 3, bgcolor: "#f9fafb", minHeight: "100vh" }}>
        <Box sx={{ maxWidth: 1200, mx: "auto", px: 3 }}>
          <Breadcrumbs separator="/" sx={{ mb: 2 }}>
            <Link
              component={RouterLink}
              to="/"
              sx={{ display: "flex", alignItems: "center", fontSize: "0.813rem", color: "#6b7280", textDecoration: "none" }}
            >
              <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
            </Link>
            <Typography sx={{ fontSize: "0.813rem", color: "#111827", fontWeight: 500, display: "flex", alignItems: "center" }}>
              <VpnKeyIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {t("Cloud Licences")}
            </Typography>
          </Breadcrumbs>
          <CLLoginCard onLogin={handleLogin} />
        </Box>
      </Box>
    )
  }

  const thStyle = {
    fontWeight: 600,
    fontSize: "0.7rem",
    color: "#6b7280",
    textTransform: "uppercase",
    py: 1.25,
    whiteSpace: "nowrap",
  }

  const tdStyle = {
    py: 1.25,
    fontSize: "0.813rem",
  }

  return (
    <Box sx={{ py: 3, bgcolor: "#f9fafb", minHeight: "100vh" }}>
      <Box sx={{ maxWidth: 1400, mx: "auto", px: 3 }}>
        {/* Breadcrumbs + Session */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Breadcrumbs separator="/" >
            <Link
              component={RouterLink}
              to="/"
              sx={{ display: "flex", alignItems: "center", fontSize: "0.813rem", color: "#6b7280", textDecoration: "none" }}
            >
              <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
            </Link>
            <Typography sx={{ fontSize: "0.813rem", color: "#111827", fontWeight: 500, display: "flex", alignItems: "center" }}>
              <VpnKeyIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {t("Cloud Licences")}
            </Typography>
          </Breadcrumbs>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: "0.75rem", color: sessionMinutes < 5 ? "#ef4444" : "#6b7280" }}>
              Session: {sessionMinutes}m
            </Typography>
            <Button
              size="small"
              startIcon={<LogoutIcon sx={{ fontSize: 14 }} />}
              onClick={handleLogout}
              sx={{ textTransform: "none", fontSize: "0.75rem", color: "#6b7280" }}
            >
              {t("Logout")}
            </Button>
          </Stack>
        </Stack>

        {/* Tabs */}
        <Paper
          elevation={0}
          sx={{ borderRadius: 2, border: "1px solid #e5e7eb", overflow: "hidden" }}
        >
          <Box sx={{ borderBottom: "1px solid #e5e7eb" }}>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              sx={{
                px: 2,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  minHeight: 52,
                },
                "& .Mui-selected": { color: "#3b82f6" },
                "& .MuiTabs-indicator": { bgcolor: "#3b82f6" },
              }}
            >
              <Tab
                icon={<VpnKeyIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={t("Licences") + ` (${stats.total})`}
              />
              <Tab
                icon={<ReceiptIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={t("Orders") + ` (${orderStats.total})`}
              />
            </Tabs>
          </Box>

          {/* ─── Licences Tab ─────────────────────────────────────── */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ px: 2, pb: 2 }}>
              {/* Analytics */}
              <Paper
                elevation={0}
                sx={{ mb: 1.5, borderRadius: 1, border: "1px solid #e5e7eb" }}
              >
                <Box
                  onClick={() => setAnalyticsExpanded(!analyticsExpanded)}
                  sx={{
                    px: 2,
                    py: 0.75,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    bgcolor: analyticsExpanded ? "#f9fafb" : "#fff",
                    "&:hover": { bgcolor: "#f9fafb" },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <BarChartIcon sx={{ fontSize: 18, color: "#6b7280" }} />
                    <Typography sx={{ fontSize: "0.813rem", fontWeight: 500 }}>
                      {t("Analytics")}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", ml: 2 }}>
                      {stats.total} {t("licences")} ·{" "}
                      {t("Active")}: {stats.active} ·{" "}
                      {t("Inactive")}: {stats.inactive}
                    </Typography>
                  </Stack>
                  <IconButton size="small">
                    {analyticsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={analyticsExpanded}>
                  <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb" }}>
                    <Stack direction="row" spacing={4} flexWrap="wrap">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <VpnKeyIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
                        <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                          {t("Total")}:{" "}
                          <strong style={{ color: "#111827" }}>{stats.total}</strong>
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LockOpenIcon sx={{ fontSize: 16, color: "#10b981" }} />
                        <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                          {t("Active")}:{" "}
                          <strong style={{ color: "#10b981" }}>{stats.active}</strong>
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <BlockIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                        <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                          {t("Inactive")}:{" "}
                          <strong style={{ color: "#ef4444" }}>{stats.inactive}</strong>
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <MemoryIcon sx={{ fontSize: 16, color: "#d97706" }} />
                        <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                          {t("HW Bound")}:{" "}
                          <strong style={{ color: "#d97706" }}>{stats.bound}</strong>
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </Collapse>
              </Paper>

              {/* Search + Filters + Actions */}
              <Paper
                elevation={0}
                sx={{
                  px: 2,
                  py: 1,
                  mb: 1.5,
                  borderRadius: 1,
                  border: "1px solid #e5e7eb",
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <TextField
                    size="small"
                    placeholder={t("Search licences...")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                      width: 240,
                      "& .MuiOutlinedInput-root": {
                        height: 32,
                        fontSize: "0.813rem",
                        bgcolor: "#f9fafb",
                        "& fieldset": { borderColor: "#e5e7eb" },
                        "&:hover fieldset": { borderColor: "#d1d5db" },
                        "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Stack direction="row" spacing={0.5}>
                    {[
                      { key: "all", label: "All" },
                      { key: "active", label: "Active" },
                      { key: "inactive", label: "Inactive" },
                    ].map((f) => (
                      <Chip
                        key={f.key}
                        label={t(f.label)}
                        size="small"
                        onClick={() => setStatusFilter(f.key)}
                        sx={{
                          height: 26,
                          fontSize: "0.7rem",
                          fontWeight: 500,
                          bgcolor:
                            statusFilter === f.key ? "#3b82f6" : "#f3f4f6",
                          color:
                            statusFilter === f.key ? "#fff" : "#374151",
                          "&:hover": {
                            bgcolor:
                              statusFilter === f.key ? "#2563eb" : "#e5e7eb",
                          },
                        }}
                      />
                    ))}
                  </Stack>
                  <Typography
                    sx={{ fontSize: "0.75rem", color: "#9ca3af", ml: "auto" }}
                  >
                    {filteredLicences.length} {t("results")}
                  </Typography>
                  <IconButton size="small" onClick={loadData} disabled={loading}>
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setCreateDialogOpen(true)}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      height: 30,
                      bgcolor: "#10b981",
                      "&:hover": { bgcolor: "#059669" },
                    }}
                  >
                    {t("New License")}
                  </Button>
                </Stack>
              </Paper>

              {/* Licences Table */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 1,
                  border: "1px solid #e5e7eb",
                  overflow: "hidden",
                }}
              >
                {loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      py: 6,
                    }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f9fafb" }}>
                          <TableCell sx={thStyle}>License Key</TableCell>
                          <TableCell sx={thStyle}>Tier</TableCell>
                          <TableCell sx={thStyle}>Owner</TableCell>
                          <TableCell sx={thStyle}>Email</TableCell>
                          <TableCell sx={thStyle}>Status</TableCell>
                          <TableCell sx={thStyle}>Hardware ID</TableCell>
                          <TableCell sx={thStyle}>Created</TableCell>
                          <TableCell sx={thStyle}>Expires</TableCell>
                          <TableCell sx={thStyle}>Last Validated</TableCell>
                          <TableCell sx={thStyle}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredLicences.length > 0 ? (
                          filteredLicences.map((l) => (
                            <TableRow
                              key={l.id}
                              hover
                              sx={{ "&:hover": { bgcolor: "#f9fafb" } }}
                            >
                              <TableCell sx={tdStyle}>
                                <Typography
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: "0.75rem",
                                    color: "#3b82f6",
                                    fontWeight: 600,
                                  }}
                                >
                                  {l.licenseKey}
                                </Typography>
                              </TableCell>
                              <TableCell sx={tdStyle}>
                                <TierChip tier={l.tier} />
                              </TableCell>
                              <TableCell sx={tdStyle}>
                                {l.ownerName || (
                                  <span style={{ color: "#d1d5db" }}>-</span>
                                )}
                              </TableCell>
                              <TableCell sx={tdStyle}>
                                {l.ownerEmail || (
                                  <span style={{ color: "#d1d5db" }}>-</span>
                                )}
                              </TableCell>
                              <TableCell sx={tdStyle}>
                                <StatusChip active={l.isActive} />
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...tdStyle,
                                  maxWidth: 120,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={l.hardwareId || ""}
                              >
                                {l.hardwareId ? (
                                  <Typography
                                    sx={{
                                      fontFamily: "monospace",
                                      fontSize: "0.7rem",
                                      color: "#6b7280",
                                    }}
                                  >
                                    {l.hardwareId.substring(0, 16)}...
                                  </Typography>
                                ) : (
                                  <span style={{ color: "#d1d5db" }}>
                                    Not bound
                                  </span>
                                )}
                              </TableCell>
                              <TableCell sx={tdStyle}>
                                <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
                                  {formatDate(l.createdAt)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={tdStyle}>
                                <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
                                  {l.expiresAt ? formatDate(l.expiresAt) : (
                                    <span style={{ color: "#d1d5db" }}>Never</span>
                                  )}
                                </Typography>
                              </TableCell>
                              <TableCell sx={tdStyle}>
                                <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
                                  {l.lastValidatedAt ? formatDate(l.lastValidatedAt) : (
                                    <span style={{ color: "#d1d5db" }}>Never</span>
                                  )}
                                </Typography>
                              </TableCell>
                              <TableCell sx={tdStyle}>
                                <Stack direction="row" spacing={0.5}>
                                  {l.isActive ? (
                                    <Button
                                      size="small"
                                      onClick={() =>
                                        handleToggleLicense(
                                          l.licenseKey,
                                          false
                                        )
                                      }
                                      sx={{
                                        textTransform: "none",
                                        fontSize: "0.7rem",
                                        minWidth: 0,
                                        px: 1,
                                        height: 24,
                                        color: "#ef4444",
                                        border: "1px solid #ef444440",
                                        "&:hover": {
                                          bgcolor: "#ef444410",
                                        },
                                      }}
                                    >
                                      Deactivate
                                    </Button>
                                  ) : (
                                    <Button
                                      size="small"
                                      onClick={() =>
                                        handleToggleLicense(
                                          l.licenseKey,
                                          true
                                        )
                                      }
                                      sx={{
                                        textTransform: "none",
                                        fontSize: "0.7rem",
                                        minWidth: 0,
                                        px: 1,
                                        height: 24,
                                        color: "#10b981",
                                        border: "1px solid #10b98140",
                                        "&:hover": {
                                          bgcolor: "#10b98110",
                                        },
                                      }}
                                    >
                                      Activate
                                    </Button>
                                  )}
                                  {l.hardwareId && (
                                    <Button
                                      size="small"
                                      onClick={() =>
                                        handleResetHardware(l.licenseKey)
                                      }
                                      sx={{
                                        textTransform: "none",
                                        fontSize: "0.7rem",
                                        minWidth: 0,
                                        px: 1,
                                        height: 24,
                                        color: "#d97706",
                                        border: "1px solid #d9770640",
                                        "&:hover": {
                                          bgcolor: "#d9770610",
                                        },
                                      }}
                                    >
                                      Reset HW
                                    </Button>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={10}
                              align="center"
                              sx={{ py: 6 }}
                            >
                              <VpnKeyIcon
                                sx={{
                                  fontSize: 48,
                                  color: "#d1d5db",
                                  mb: 1,
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: "0.875rem",
                                  color: "#9ca3af",
                                }}
                              >
                                {t("No licences found")}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Box>
          </TabPanel>

          {/* ─── Orders Tab ───────────────────────────────────────── */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ px: 2, pb: 2 }}>
              {/* Order Stats */}
              <Paper
                elevation={0}
                sx={{ mb: 1.5, borderRadius: 1, border: "1px solid #e5e7eb", p: 2 }}
              >
                <Stack direction="row" spacing={4} flexWrap="wrap">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ReceiptIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
                    <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                      {t("Total")}:{" "}
                      <strong style={{ color: "#111827" }}>{orderStats.total}</strong>
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LockOpenIcon sx={{ fontSize: 16, color: "#10b981" }} />
                    <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                      {t("Completed")}:{" "}
                      <strong style={{ color: "#10b981" }}>{orderStats.completed}</strong>
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <BlockIcon sx={{ fontSize: 16, color: "#d97706" }} />
                    <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                      {t("Pending")}:{" "}
                      <strong style={{ color: "#d97706" }}>{orderStats.pending}</strong>
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                      {t("Revenue")}:{" "}
                      <strong style={{ color: "#111827" }}>
                        €{orderStats.revenue.toFixed(2)}
                      </strong>
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>

              {/* Order Search */}
              <Paper
                elevation={0}
                sx={{
                  px: 2,
                  py: 1,
                  mb: 1.5,
                  borderRadius: 1,
                  border: "1px solid #e5e7eb",
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    size="small"
                    placeholder={t("Search orders...")}
                    value={orderSearch}
                    onChange={(e) => setOrderSearch(e.target.value)}
                    sx={{
                      width: 240,
                      "& .MuiOutlinedInput-root": {
                        height: 32,
                        fontSize: "0.813rem",
                        bgcolor: "#f9fafb",
                        "& fieldset": { borderColor: "#e5e7eb" },
                        "&:hover fieldset": { borderColor: "#d1d5db" },
                        "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography
                    sx={{ fontSize: "0.75rem", color: "#9ca3af", ml: "auto" }}
                  >
                    {filteredOrders.length} {t("results")}
                  </Typography>
                  <IconButton size="small" onClick={loadData} disabled={loading}>
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Stack>
              </Paper>

              {/* Orders Table */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 1,
                  border: "1px solid #e5e7eb",
                  overflow: "hidden",
                }}
              >
                {loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      py: 6,
                    }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f9fafb" }}>
                          <TableCell sx={thStyle}>Order ID</TableCell>
                          <TableCell sx={thStyle}>Tier</TableCell>
                          <TableCell sx={thStyle}>Customer</TableCell>
                          <TableCell sx={thStyle}>Email</TableCell>
                          <TableCell sx={thStyle}>Amount</TableCell>
                          <TableCell sx={thStyle}>Status</TableCell>
                          <TableCell sx={thStyle}>License Key</TableCell>
                          <TableCell sx={thStyle}>Created</TableCell>
                          <TableCell sx={thStyle}>Completed</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredOrders.length > 0 ? (
                          filteredOrders.map((o) => (
                            <TableRow
                              key={o.id}
                              hover
                              sx={{ "&:hover": { bgcolor: "#f9fafb" } }}
                            >
                              <TableCell sx={tdStyle}>
                                <Typography
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: "0.75rem",
                                    color: "#3b82f6",
                                  }}
                                >
                                  {o.orderId}
                                </Typography>
                              </TableCell>
                              <TableCell sx={tdStyle}>
                                <TierChip tier={o.tier} />
                              </TableCell>
                              <TableCell sx={tdStyle}>
                                {o.customerName || (
                                  <span style={{ color: "#d1d5db" }}>-</span>
                                )}
                              </TableCell>
                              <TableCell sx={tdStyle}>{o.customerEmail}</TableCell>
                              <TableCell sx={tdStyle}>
                                <Typography sx={{ fontSize: "0.813rem", fontWeight: 500 }}>
                                  €{(o.totalAmount || 0).toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={tdStyle}>
                                <Chip
                                  label={o.status}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: "0.7rem",
                                    fontWeight: 600,
                                    bgcolor:
                                      o.status === "completed"
                                        ? "#10b98118"
                                        : "#d9770618",
                                    color:
                                      o.status === "completed"
                                        ? "#10b981"
                                        : "#d97706",
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={tdStyle}>
                                {o.licenseKey ? (
                                  <Typography
                                    sx={{
                                      fontFamily: "monospace",
                                      fontSize: "0.7rem",
                                      color: "#6b7280",
                                    }}
                                  >
                                    {o.licenseKey}
                                  </Typography>
                                ) : (
                                  <span style={{ color: "#d1d5db" }}>-</span>
                                )}
                              </TableCell>
                              <TableCell sx={tdStyle}>
                                <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
                                  {formatDate(o.createdAt)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={tdStyle}>
                                <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
                                  {o.completedAt ? formatDate(o.completedAt) : (
                                    <span style={{ color: "#d1d5db" }}>-</span>
                                  )}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={9}
                              align="center"
                              sx={{ py: 6 }}
                            >
                              <ReceiptIcon
                                sx={{
                                  fontSize: 48,
                                  color: "#d1d5db",
                                  mb: 1,
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: "0.875rem",
                                  color: "#9ca3af",
                                }}
                              >
                                {t("No orders found")}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Box>
          </TabPanel>
        </Paper>

        {/* Create License Dialog */}
        <CreateLicenseDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onCreated={(license) => {
            setSnackbar({
              open: true,
              message: `License created: ${license.licenseKey}`,
              severity: "success",
            })
            loadData()
          }}
          tiers={tiers}
        />

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  )
}

export default MuiCloudLicences
